import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { IconSparkles } from '../ui/icons';
import type { DialMilestone } from './useDialMilestoneTrigger';

export type PacingMilestoneDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	milestone: DialMilestone | null;
};

export const PacingMilestoneDialog: FC<PacingMilestoneDialogProps> = ({ open, onOpenChange, milestone }) => {
	if (!milestone) return null;

	const { dialsToday, connectsToday, demosBookedToday, dialGoal } = milestone;
	const dialsLine = dialGoal != null ? `${dialsToday} / ${dialGoal} dials` : `${dialsToday} dials`;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<div className={css(styles.iconRow)}>
						<span className={css(styles.iconBadge)}>
							<IconSparkles size={18} sw={2} />
						</span>
					</div>
					<DialogTitle>{dialsToday} dials — keep going.</DialogTitle>
					<DialogDescription>Here's where you are today.</DialogDescription>
				</DialogHeader>
				<DialogBody>
					<div className={css(styles.statsGrid)}>
						<Stat label='Dials' value={dialsLine} />
						<Stat label='Connects' value={String(connectsToday)} />
						<Stat label={demosBookedToday === 1 ? 'Demo' : 'Demos'} value={String(demosBookedToday)} />
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant='primary' size='lg' onClick={() => onOpenChange(false)}>
						Keep going
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const Stat: FC<{ label: string; value: string }> = ({ label, value }) => (
	<div className={css(styles.stat)}>
		<div className={css(styles.statValue)}>{value}</div>
		<div className={css(styles.statLabel)}>{label}</div>
	</div>
);

const styles = StyleSheet.create({
	popup: {
		maxWidth: 440,
	},
	iconRow: {
		display: 'flex',
		justifyContent: 'center',
		marginBottom: 8,
	},
	iconBadge: {
		width: 44,
		height: 44,
		borderRadius: 12,
		background: 'var(--accent-soft)',
		color: 'var(--accent-ink)',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	statsGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: 12,
		marginTop: 4,
	},
	stat: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 4,
		padding: '12px 8px',
		borderRadius: 10,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
	},
	statValue: {
		fontSize: 20,
		fontWeight: 700,
		color: 'var(--ink)',
	},
	statLabel: {
		fontSize: 11,
		fontWeight: 500,
		color: 'var(--ink-muted)',
		textTransform: 'uppercase',
		letterSpacing: '0.06em',
	},
});
